import React, { useState, JSX } from 'react';
import {
  Button,
  CardSubtitle,
  CardText,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row
} from 'reactstrap';
import { ApiAgency, ApiBusinessPartnerContact } from '@ibe/api';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import fallback from '../../../_Translations/generated/agency-profile-contact.en.t.json';
import Keys from '../../../_Translations/generated/agency-profile-contact.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';

interface Props {
  agency: ApiAgency;
  onSubmit: (agency: ApiBusinessPartnerContact) => void;
}

const AgencyProfileContactForm = (props: Props): JSX.Element => {
  const { agency, onSubmit } = props;
  const [isEditing, setIsEditing] = useState(false);
  const { t } = useTranslation('agency-profile-contact', fallback, Keys);
  const schema = yup.object().shape({
    website: yup.string().optional(),
    phone: yup.string().required(t(Keys.requiredField))
  });
  const defaultValues = {
    website: agency.website,
    phone: agency.communicationDetails.phone
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { ...defaultValues }
  });

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <Form
      onSubmit={handleSubmit((businessPartnerContact: ApiBusinessPartnerContact): void => {
        onSubmit(businessPartnerContact);
        setIsEditing(false);
      })}
    >
      <Row>
        <Col md={6}>
          <FormGroup>
            <Label className="font-weight-light" htmlFor="phone">
              {t(Keys.phone)}
            </Label>
            <Input
              type="text"
              id="phone"
              placeholder={t(Keys.phone)}
              disabled={!isEditing}
              invalid={!!errors.phone}
              {...register('phone')}
              innerRef={register('phone').ref}
              onChange={(e): void => setValue('phone', e.target.value)}
              autoComplete={'tel'}
            />
            {errors.phone && (
              <FormFeedback>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {errors.phone.message}
              </FormFeedback>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label className="font-weight-light" htmlFor="website">
              {t(Keys.website)}
            </Label>
            <Input
              type="text"
              id="website"
              placeholder={t(Keys.website)}
              disabled={!isEditing}
              invalid={!!errors.website}
              {...register('website')}
              innerRef={register('website').ref}
              onChange={(e): void => setValue('website', e.target.value)}
              autoComplete={'url'}
            />
            {errors.website && (
              <FormFeedback>
                <FontAwesomeIcon icon={faExclamationTriangle} />
                {errors.website.message}
              </FormFeedback>
            )}
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <FormGroup>
            <CardSubtitle tag="h6" className="font-weight-light">
              {t(Keys.email)}
            </CardSubtitle>
            <CardText>{agency.communicationDetails.email}</CardText>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col>
          {!isEditing && (
            <Button color="primary" onClick={(): void => setIsEditing(true)}>
              {t(Keys.edit)}
            </Button>
          )}
          {isEditing && (
            <>
              <Button
                color="secondary"
                onClick={(): void => {
                  setIsEditing(false);
                  reset({ ...defaultValues });
                }}
              >
                {t(Keys.cancel)}
              </Button>
              <Button className="ml-3" color="primary" type="submit">
                {t(Keys.save)}
              </Button>
            </>
          )}
        </Col>
      </Row>
    </Form>
  );
};
export default AgencyProfileContactForm;
